import {
  AfterViewInit,
  Component,
  computed,
  inject,
  Input,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { BookmarkStore } from '../../../stores/bookmark/bookmark.state';
import { Story } from '../../../models/story';
import { GalleriaModule } from 'primeng/galleria';
import { StoryStore } from '../../../stores/story/story.state';
import { AtlasGridComponent } from '../atlas-grid/atlas-grid.component';
import { GlobalsStore } from '../../../stores/globals.state';
import { Button, ButtonDirective } from 'primeng/button';
import { Bookmark } from '../../../models/bookmark';
import { HeaderComponent } from '../../atoms/header/header.component';
import { RoutingService } from '../../../services/routing.service';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { DropdownModule } from 'primeng/dropdown';
import { FormsModule } from '@angular/forms';
import { MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { WidgetStore } from '../../../stores/grid/widget.state';
import { GridStore } from '../../../stores/grid/grid.state';

@Component({
  selector: 'app-story-gallery',
  standalone: true,
  imports: [
    GalleriaModule,
    AtlasGridComponent,
    ButtonDirective,
    HeaderComponent,
    Button,
    DropdownModule,
    FormsModule,
    MenuModule,
  ],
  // Own grid/widget stores for the slides: the root instances belong to the
  // view open behind the gallery, and AtlasGridComponent#buildSaveableLayout
  // persists everything in the WidgetStore - slide widgets left in there
  // would end up in that view on its next save.
  providers: [WidgetStore, GridStore],
  templateUrl: './story-gallery.component.html',
  styleUrl: './story-gallery.component.scss',
})
export class StoryGalleryComponent implements OnInit, AfterViewInit {
  readonly globalsStore = inject(GlobalsStore);
  readonly storyStore = inject(StoryStore);
  readonly bookmarkStore = inject(BookmarkStore);
  @ViewChild('galleria') galleria!: any;
  private maskElement: HTMLElement | null = null; // Cache mask element for performance
  @Input() storyId!: string;
  galleriaVisible = true;
  story!: Story;
  slides: Bookmark[] = [];
  slideMenuItems: MenuItem[] = [];

  constructor(
    private routingService: RoutingService,
    private confirmationDialogService: ConfirmationDialogService
  ) {}

  currentIndex = signal(0);

  content = computed(() =>
    this.bookmarkStore.getById(this.slides[this.currentIndex()]._id)
  );

  goToSlide(id: string): void {
    const idx = this.slides.findIndex(slide => slide._id === id);
    this.onSlide(idx);
  }

  /**
   * Updates the current slide index to the specified value.
   *
   * @param {number} idx - The index of the slide to update to.
   * @return {void} This method does not return a value.
   */
  onSlide(idx: number): void {
    this.currentIndex.update(value => idx);

    this.scrollTop();
  }

  /**
   * Scrolls the `maskElement` property to the top smoothly after a 200ms delay.
   * Ensures the `maskElement` is scrolled to its top position with a smooth scrolling effect.
   *
   * @return {void} Does not return a value.
   */
  scrollTop(): void {
    setTimeout(() => {
      if (this.maskElement) {
        this.maskElement.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 200);
  }

  /**
   * Lifecycle hook method that is called after the component's view and child views have been initialized.
   * This implementation calls the `cacheMaskElement` method to perform necessary operations after the view initialization.
   * @return {void} No return value.
   */
  ngAfterViewInit(): void {
    this.cacheMaskElement();

    this.slideMenuItems = this.slides.map((slide, idx) => ({
      label: slide.title,
      command: () => this.onSlide(idx),
      icon: 'fas fa-note-sticky',
    }));
  }

  /**
   * Caches the mask element of the galleria component for later use.
   * It attempts to retrieve the element with the class name 'p-galleria-mask' from the galleria's native element.
   * If the galleria native element or the mask element is not found, a warning is logged to the console.
   *
   * @return {void} This method does not return any value.
   */
  // NG21-MIGRATION(stufe-2): greift per querySelector auf PrimeNG-DOM (.p-galleria-mask) zu -> Klassenname in v21 prüfen (§8.3)
  private cacheMaskElement(): void {
    const nativeElement = this.galleria?.element?.nativeElement;
    if (nativeElement) {
      this.maskElement = nativeElement.querySelector('.p-galleria-mask');
      if (!this.maskElement) {
        console.warn('p-galleria-mask element not found.');
      }
    } else {
      console.warn('Galleria nativeElement is undefined.');
    }
  }

  /**
   * Closes the Galleria component by setting its visibility to false
   * and triggers the onClose callback function.
   *
   * @return {void} Does not return any value.
   */
  close() {
    this.galleriaVisible = false;
    this.onClose();
  }

  /**
   * Handles the closure process by invoking the `closeActiveStory` method
   * from the `globalsStore` to close the currently active story.
   *
   * @return {void} Does not return any value.
   */
  onClose(): void {
    this.globalsStore.closeActiveStory();
  }

  /**
   * Opens a specified bookmark by navigating to its route and performs additional actions upon completion.
   *
   * @param {Bookmark} bookmark - The bookmark object containing the details of the bookmark to be opened.
   * @param {boolean} newTab - Open the Bookmark in a new Tab.
   * @return {void} This method does not return a value.
   */
  openBookmark(bookmark: Bookmark, newTab = false): void {
    this.routingService.openBookmarkRoute(bookmark._id, newTab).then(() => {
      if (!newTab) {
        this.globalsStore.closeActiveStory();
      }
    });
  }

  /**
   * Copies the story link to the clipboard.
   *
   * @param {string} storyId - The unique identifier of the story for which the link will be generated.
   * @return {void} This method does not return a value.
   */
  copyStoryLink(storyId: string): void {
    const domain = window.location.origin;
    const route = this.routingService.getStoryRoute(storyId);

    navigator.clipboard.writeText(`${domain}${route}`).then(() => {
      this.confirmationDialogService.showMessage('Link kopiert!');
    });
  }

  /**
   * Initializes the component after Angular has fully set up the component's data-bound properties.
   * Fetches the story object using the provided story ID and initializes the slides with the bookmarks from the story.
   *
   * @return {void} This method does not return a value.
   */
  ngOnInit(): void {
    this.story = this.storyStore.getById(this.storyId);
    this.slides = this.bookmarkStore.getByIds(
      this.story.bookmarks.filter(bookmarkId =>
        this.bookmarkStore.has(bookmarkId)
      )
    );
  }
}
